import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const DEFAULTS = {
  cardsPerSession: 10,
  autoFlipEnabled: false,
  autoFlipDelay: 5,
};

// Bouton de valeur rapide — plein quand sélectionné.
const Preset = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`py-2 rounded-lg text-sm font-medium tabular-nums border transition-colors ${
      active
        ? 'bg-ink-900 text-onAccent border-ink-900'
        : 'bg-surface text-ink-700 border-sand-300 hover:border-ink-400'
    }`}
  >
    {children}
  </button>
);

const Settings = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [settings, setSettings] = useState(DEFAULTS);
  const [saveMessage, setSaveMessage] = useState('');

  // Charger les paramètres depuis localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('flashcardSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  // Sauvegarder automatiquement à chaque changement
  const handleChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('flashcardSettings', JSON.stringify(newSettings));
    setSaveMessage('Sauvegardé');
    setTimeout(() => setSaveMessage(''), 2000);
  };

  const handleReset = () => {
    setSettings(DEFAULTS);
    localStorage.setItem('flashcardSettings', JSON.stringify(DEFAULTS));
    setSaveMessage('Réinitialisé');
    setTimeout(() => setSaveMessage(''), 2000);
  };

  const slider =
    'w-full h-1 bg-sand-200 rounded-full appearance-none cursor-pointer accent-clay-500';

  return (
    <div className="min-h-screen pb-28">
      <header className="max-w-2xl mx-auto px-5 pt-10 pb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="-ml-1.5 p-1.5 text-ink-500 hover:text-ink-900 transition-colors"
            aria-label="Retour"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="font-display text-[2rem] leading-none text-ink-900">
            Paramètres
          </h1>
        </div>
        <p className="label-caps mt-2 ml-8 h-4 transition-opacity">
          {saveMessage || 'Sauvegarde automatique'}
        </p>
      </header>

      <main className="max-w-2xl mx-auto px-5">
        {/* Cartes par session */}
        <section className="py-8 border-t border-sand-200">
          <div className="flex items-baseline justify-between gap-4">
            <div>
              <h2 className="font-medium text-ink-900">Cartes par session</h2>
              <p className="text-sm text-ink-500 mt-1">
                La longueur d'une séance de révision.
              </p>
            </div>
            <span className="font-display text-3xl text-ink-900 tabular-nums shrink-0">
              {settings.cardsPerSession}
            </span>
          </div>

          <input
            type="range"
            min="5"
            max="50"
            step="5"
            value={settings.cardsPerSession}
            onChange={(e) => handleChange('cardsPerSession', parseInt(e.target.value))}
            className={`${slider} mt-6`}
          />
          <div className="flex justify-between label-caps mt-2">
            <span>5</span>
            <span>25</span>
            <span>50</span>
          </div>

          <div className="grid grid-cols-4 gap-2 mt-5">
            {[10, 20, 30, 50].map((num) => (
              <Preset
                key={num}
                active={settings.cardsPerSession === num}
                onClick={() => handleChange('cardsPerSession', num)}
              >
                {num}
              </Preset>
            ))}
          </div>
        </section>

        {/* Retournement automatique */}
        <section className="py-8 border-t border-sand-200">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-medium text-ink-900">Retournement automatique</h2>
              <p className="text-sm text-ink-500 mt-1">
                La réponse se dévoile seule après un délai. Utile pour tenir
                un rythme régulier.
              </p>
            </div>

            <button
              onClick={() => handleChange('autoFlipEnabled', !settings.autoFlipEnabled)}
              role="switch"
              aria-checked={settings.autoFlipEnabled}
              className={`relative shrink-0 inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                settings.autoFlipEnabled ? 'bg-clay-500' : 'bg-sand-300'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-ink-900 shadow-card transition-transform ${
                  settings.autoFlipEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {settings.autoFlipEnabled && (
            <div className="mt-6 pt-6 border-t border-sand-200">
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-sm text-ink-500">Délai avant retournement</span>
                <span className="font-display text-2xl text-ink-900 tabular-nums">
                  {settings.autoFlipDelay}s
                </span>
              </div>

              <input
                type="range"
                min="3"
                max="15"
                step="1"
                value={settings.autoFlipDelay}
                onChange={(e) => handleChange('autoFlipDelay', parseInt(e.target.value))}
                className={`${slider} mt-5`}
              />
              <div className="flex justify-between label-caps mt-2">
                <span>3s rapide</span>
                <span>9s moyen</span>
                <span>15s lent</span>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-5">
                {[5, 8, 10].map((num) => (
                  <Preset
                    key={num}
                    active={settings.autoFlipDelay === num}
                    onClick={() => handleChange('autoFlipDelay', num)}
                  >
                    {num}s
                  </Preset>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Note */}
        <section className="py-8 border-t border-sand-200">
          <h2 className="label-caps mb-3">Bon à savoir</h2>
          <p className="text-ink-500 leading-relaxed">
            Ces réglages sont propres à cet appareil et s'appliquent
            immédiatement. Ils ne changent rien à la planification des cartes :
            l'ordre et l'échéance des révisions restent pilotés par l'algorithme
            FSRS depuis Notion.
          </p>
        </section>

        <div className="pt-2 space-y-3">
          <button
            onClick={handleReset}
            className="w-full py-3.5 border border-sand-300 rounded-xl text-ink-700
                       hover:border-ink-400 transition-colors"
          >
            Rétablir les valeurs par défaut
          </button>

          <button
            onClick={() => {
              logout();
              navigate('/login');
            }}
            className="w-full py-3.5 border border-sand-300 rounded-xl text-ink-700
                       hover:border-rating-forgot hover:text-rating-forgot transition-colors"
          >
            Se déconnecter
          </button>
        </div>
      </main>
    </div>
  );
};

export default Settings;
